import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtINR, fmtDateTime } from "../lib/api";
import { StatusPill, Field } from "../components/Primitives";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Send, Check, Wrench, Clock, Package, FileText, ShieldPlus } from "lucide-react";

const LINE_DEFAULT = { type: "labor", description: "", qty: 1, rate: 0, discount: 0, gst_rate: 18, item_id: "" };

export default function JobCardDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [jc, setJc] = useState(null);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bays, setBays] = useState([]);
  const [otp, setOtp] = useState("");
  const [otpShown, setOtpShown] = useState(null);
  const [advance, setAdvance] = useState(0);
  const [workLog, setWorkLog] = useState("");
  const [issueItem, setIssueItem] = useState({ item_id: "", qty: 1 });

  const load = async () => {
    const { data } = await api.get(`/job-cards/${id}`);
    setJc(data);
    setAdvance(data.advance_required);
  };
  useEffect(() => {
    load();
    api.get("/stores/items").then(({ data }) => setItems(data));
    api.get("/employees").then(({ data }) => setEmployees(data));
    api.get("/bays").then(({ data }) => setBays(data));
  }, [id]);

  if (!jc) return <div>Loading…</div>;

  const setLine = (idx, patch) => {
    const lines = [...jc.lines];
    lines[idx] = { ...lines[idx], ...patch };
    if (patch.item_id) {
      const item = items.find((i) => i.id === patch.item_id);
      if (item) {
        lines[idx].description = item.name;
        lines[idx].rate = item.sell_price;
        lines[idx].gst_rate = item.gst_rate;
      }
    }
    setJc({ ...jc, lines });
  };
  const addLine = (type) => setJc({ ...jc, lines: [...jc.lines, { ...LINE_DEFAULT, type }] });
  const delLine = (idx) => setJc({ ...jc, lines: jc.lines.filter((_, i) => i !== idx) });

  const saveJC = async () => {
    try {
      await api.put(`/job-cards/${id}`, {
        intake_id: jc.intake_id,
        technician_id: jc.technician_id,
        bay_id: jc.bay_id,
        advance_required: +jc.advance_required,
        lines: jc.lines.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, discount: +l.discount, gst_rate: +l.gst_rate })),
        estimate_notes: jc.estimate_notes,
        warranty_notes: jc.warranty_notes,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const requestApproval = async () => {
    await saveJC();
    const { data } = await api.post(`/job-cards/${id}/request-approval`);
    setOtpShown(data.otp_for_demo);
    toast.success("OTP generated — share with customer");
  };

  const verifyApproval = async () => {
    try {
      await api.post(`/job-cards/${id}/verify-approval`, { job_card_id: id, otp });
      toast.success("Approved by customer");
      setOtp(""); setOtpShown(null); load();
    } catch (e) { toast.error("Invalid OTP"); }
  };

  const payAdvance = async () => {
    const fd = new FormData(); fd.append("amount", advance); fd.append("mode", "cash");
    await api.post(`/job-cards/${id}/advance`, fd);
    toast.success("Advance recorded"); load();
  };

  const addWorkLog = async () => {
    if (!workLog) return;
    const fd = new FormData(); fd.append("note", workLog);
    await api.post(`/job-cards/${id}/work-log`, fd);
    setWorkLog(""); load();
  };

  const issueFromStore = async () => {
    if (!issueItem.item_id) return;
    try {
      const selected = items.find((i) => i.id === issueItem.item_id);
      await api.post("/stores/movements", {
        item_id: issueItem.item_id, direction: "out", qty: +issueItem.qty,
        reason: `Issued to ${jc.code}`, job_card_id: id,
      });
      // Also auto-add a line in JC if not present
      const existing = jc.lines.find((l) => l.item_id === issueItem.item_id);
      if (!existing && selected) {
        jc.lines.push({ type: "part", description: selected.name, item_id: selected.id,
                        qty: +issueItem.qty, rate: selected.sell_price, discount: 0,
                        gst_rate: selected.gst_rate, issued_qty: +issueItem.qty });
        await saveJC();
      }
      toast.success("Issued from store");
      setIssueItem({ item_id: "", qty: 1 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deliver = async () => {
    const fd = new FormData();
    fd.append("checklist", JSON.stringify({
      exterior_clean: true, interior_clean: true, keys_handed: true,
      customer_signature: true, invoice_issued: true,
    }));
    await api.post(`/job-cards/${id}/deliver`, fd);
    toast.success("Delivered — photos compressed to save space");
    load();
  };

  const createInvoice = async () => {
    try {
      const inv = {
        kind: "customer", customer_name: jc.customer_name, customer_phone: jc.customer_phone,
        job_card_id: id, vehicle_reg: jc.registration_no,
        lines: jc.lines.map((l) => ({
          description: l.description, qty: +l.qty, rate: +l.rate, discount: +l.discount, gst_rate: +l.gst_rate,
        })),
      };
      const { data } = await api.post("/invoices", inv);
      toast.success("Invoice created");
      nav(`/accounting?highlight=${data.id}`);
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="kk-btn-ghost mb-4" data-testid="back"><ArrowLeft size={14} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[var(--kk-border)]">
        <div>
          <div className="kk-overline">Job Card</div>
          <div className="font-display text-3xl uppercase">{jc.code}</div>
          <div className="text-sm mt-1">{jc.make} {jc.model} · {jc.registration_no}</div>
          <div className="text-xs text-[var(--kk-muted)]">{jc.customer_name} · {jc.customer_phone}</div>
        </div>
        <div className="text-right">
          <StatusPill status={jc.status} />
          <div className="text-xs text-[var(--kk-muted)] mt-1 font-mono">{fmtDateTime(jc.created_at)}</div>
          <div className="font-display text-xl mt-2">{fmtINR(jc.totals?.grand_total)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Assignment */}
          <div className="kk-card p-5">
            <div className="font-display text-sm uppercase mb-3">Assignment</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Technician">
                <select className="kk-input" value={jc.technician_id || ""} onChange={(e) => setJc({ ...jc, technician_id: e.target.value })}>
                  <option value="">—</option>
                  {employees.filter((e) => e.role === "mechanic").map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
              <Field label="Bay">
                <select className="kk-input" value={jc.bay_id || ""} onChange={(e) => setJc({ ...jc, bay_id: e.target.value })}>
                  <option value="">—</option>
                  {bays.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Advance required (₹)">
                <input className="kk-input" type="number" value={jc.advance_required} onChange={(e) => setJc({ ...jc, advance_required: e.target.value })} />
              </Field>
              <Field label="Warranty / Insurance notes">
                <input className="kk-input" value={jc.warranty_notes || ""} onChange={(e) => setJc({ ...jc, warranty_notes: e.target.value })} />
              </Field>
            </div>
          </div>

          {/* Lines */}
          <div className="kk-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-sm uppercase">Estimate & Job Lines</div>
              <div className="flex gap-2">
                <button onClick={() => addLine("labor")} className="kk-pill kk-pill-black" data-testid="add-labor"><Plus size={12}/> Labor</button>
                <button onClick={() => addLine("part")} className="kk-pill kk-pill-black" data-testid="add-part"><Plus size={12}/> Part</button>
                <button onClick={() => addLine("sublet")} className="kk-pill kk-pill-black"><Plus size={12}/> Sublet</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="kk-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>GST%</th>
                    <th>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jc.lines.map((l, idx) => {
                    const amt = (+l.qty) * (+l.rate) - (+l.discount || 0);
                    return (
                      <tr key={idx}>
                        <td>
                          <select value={l.type} onChange={(e) => setLine(idx, { type: e.target.value })} className="kk-input py-1 text-xs">
                            <option value="labor">Labor</option>
                            <option value="part">Part</option>
                            <option value="sublet">Sublet</option>
                          </select>
                        </td>
                        <td>
                          {l.type === "part" ? (
                            <select value={l.item_id || ""} onChange={(e) => setLine(idx, { item_id: e.target.value })} className="kk-input py-1 text-xs">
                              <option value="">— pick part —</option>
                              {items.map((i) => <option key={i.id} value={i.id}>{`${i.name} · ${i.sku}`}</option>)}
                            </select>
                          ) : (
                            <input className="kk-input py-1 text-sm" value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })} />
                          )}
                        </td>
                        <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} /></td>
                        <td><input className="kk-input py-1 text-sm w-24" type="number" value={l.rate} onChange={(e) => setLine(idx, { rate: e.target.value })} /></td>
                        <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.gst_rate} onChange={(e) => setLine(idx, { gst_rate: e.target.value })} /></td>
                        <td className="font-mono">{fmtINR(amt)}</td>
                        <td><button onClick={() => delLine(idx)}><Trash2 size={14}/></button></td>
                      </tr>
                    );
                  })}
                  {jc.lines.length === 0 && <tr><td colSpan={7} className="text-center text-[var(--kk-muted)] py-4">No lines — add labor or parts.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-8 mt-4 text-sm">
              <div>
                <div className="kk-overline">Subtotal</div>
                <div className="font-mono">{fmtINR(jc.totals?.subtotal)}</div>
              </div>
              <div>
                <div className="kk-overline">GST</div>
                <div className="font-mono">{fmtINR((jc.totals?.cgst || 0) + (jc.totals?.sgst || 0) + (jc.totals?.igst || 0))}</div>
              </div>
              <div>
                <div className="kk-overline">Grand</div>
                <div className="font-display text-lg">{fmtINR(jc.totals?.grand_total)}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button className="kk-btn-primary" onClick={saveJC} data-testid="save-jc">Save</button>
              {jc.status === "draft" && (
                <button className="kk-btn-ghost" onClick={requestApproval} data-testid="req-approval"><Send size={14}/> Request Approval</button>
              )}
            </div>
            {otpShown && (
              <div className="mt-4 p-3 bg-[var(--kk-lime)] text-[var(--kk-black)] text-sm">
                <strong>OTP sent (demo):</strong> <span className="font-mono">{otpShown}</span> — share with customer to approve.
              </div>
            )}
            {jc.status === "pending_approval" && (
              <div className="mt-4 flex gap-2 items-end">
                <Field label="Customer OTP"><input className="kk-input" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="otp-input" /></Field>
                <button className="kk-btn-primary" onClick={verifyApproval} data-testid="verify-otp"><Check size={14} /> Verify</button>
              </div>
            )}
            {jc.status === "approved" && (
              <div className="mt-4 flex gap-2 items-end">
                <Field label="Advance amount">
                  <input className="kk-input" type="number" value={advance} onChange={(e) => setAdvance(e.target.value)} />
                </Field>
                <button className="kk-btn-primary" onClick={payAdvance} data-testid="pay-advance">Record Advance · Start Work</button>
              </div>
            )}
          </div>

          {/* Work progress */}
          {["in_progress", "ready", "delivered"].includes(jc.status) && (
            <div className="kk-card p-5">
              <div className="font-display text-sm uppercase mb-3">Work Progress</div>

              <div className="grid md:grid-cols-3 gap-3 items-end">
                <Field label="Issue Part">
                  <select className="kk-input" value={issueItem.item_id} onChange={(e) => setIssueItem({ ...issueItem, item_id: e.target.value })}>
                    <option value="">— part —</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{`${i.name} · stk ${i.qty}`}</option>)}
                  </select>
                </Field>
                <Field label="Qty"><input className="kk-input" type="number" value={issueItem.qty} onChange={(e) => setIssueItem({ ...issueItem, qty: e.target.value })} /></Field>
                <button className="kk-btn-primary" onClick={issueFromStore} data-testid="issue-part"><Package size={14}/> Issue</button>
              </div>

              <div className="mt-5 grid md:grid-cols-3 gap-3 items-end">
                <div className="md:col-span-2">
                  <Field label="Work log"><input className="kk-input" placeholder="e.g. Replaced brake pads, cleared DTC." value={workLog} onChange={(e) => setWorkLog(e.target.value)} /></Field>
                </div>
                <button className="kk-btn-ghost" onClick={addWorkLog} data-testid="add-worklog"><Clock size={14}/> Add Log</button>
              </div>

              <div className="mt-5">
                <div className="kk-overline mb-2">Timeline</div>
                <ul className="space-y-2">
                  {(jc.stock_moves || []).map((m) => (
                    <li key={m.id} className="flex gap-3 text-sm">
                      <span className="kk-timeline-node mt-2"></span>
                      <div>
                        <div className="font-mono text-xs">{fmtDateTime(m.created_at)}</div>
                        <div><Package size={11} className="inline" /> Issued {m.qty} × {m.item_name} <span className="text-[var(--kk-muted)]">({m.item_sku})</span></div>
                      </div>
                    </li>
                  ))}
                  {(jc.work_logs || []).map((w, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="kk-timeline-node mt-2"></span>
                      <div>
                        <div className="font-mono text-xs">{fmtDateTime(w.at)} · {w.by}</div>
                        <div><Wrench size={11} className="inline" /> {w.note}</div>
                      </div>
                    </li>
                  ))}
                  {(jc.stock_moves || []).length === 0 && (jc.work_logs || []).length === 0 && (
                    <div className="text-xs text-[var(--kk-muted)]">No activity yet.</div>
                  )}
                </ul>
              </div>

              <div className="mt-6 flex gap-2 flex-wrap">
                {jc.status !== "delivered" && (
                  <button className="kk-btn-primary" onClick={deliver} data-testid="deliver-jc"><Check size={14}/> Deliver & Compress Photos</button>
                )}
                <button className="kk-btn-ghost" onClick={createInvoice} data-testid="create-invoice"><FileText size={14}/> Create Invoice</button>
                <button className="kk-btn-ghost" onClick={() => nav(`/warranty?jobcard=${jc.id}`)} data-testid="jc-add-warranty"><ShieldPlus size={14}/> Add Warranty</button>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="kk-card p-5">
            <div className="kk-overline">Customer</div>
            <div className="font-semibold mt-1">{jc.customer_name}</div>
            <div className="text-xs">{jc.customer_phone}</div>
            <div className="kk-overline mt-4">Vehicle</div>
            <div className="text-sm">{jc.make} {jc.model}</div>
            <div className="text-xs font-mono">{jc.registration_no}</div>
            <div className="kk-overline mt-4">Advance</div>
            <div className="font-mono text-sm">{fmtINR(jc.advance_paid)} / {fmtINR(jc.advance_required)}</div>
            {jc.approval_otp && (
              <>
                <div className="kk-overline mt-4">Pending OTP</div>
                <div className="font-mono">{jc.approval_otp}</div>
              </>
            )}
          </div>

          {jc.delivery_checklist && (
            <div className="kk-card p-5">
              <div className="kk-overline">Delivery Checklist</div>
              <ul className="text-sm mt-2 space-y-1">
                {Object.entries(jc.delivery_checklist).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g, " ")}</span><span>{v ? "✓" : "—"}</span></li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
